/**
 * Nginx SSL Apply Button
 * Shows in header when there are pending SSL configuration changes
 */

import { useState, useCallback } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useNginxConfigStore } from '../../store/nginxConfig';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';

export function NginxApplyButton() {
    const { t } = useI18n();
    const { user } = useAuthStore();
    const { showSuccess, showError } = useNotifications();
    const tenantSlug = user?.tenant?.slug ?? 'main';

    const { hasPendingChanges, clearPendingChanges } = useNginxConfigStore();
    const [isApplying, setIsApplying] = useState(false);

    const handleApply = useCallback(async () => {
        setIsApplying(true);

        try {
            const response = await fetch(buildApiUrl('/api/maintenance/nginx/regenerate'), {
                method: 'POST',
                headers: getApiHeaders(tenantSlug, user?.id),
                body: JSON.stringify({})
            });

            const data = await response.json();

            if (data.success) {
                clearPendingChanges();
                const isIpAccess = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(window.location.hostname);
                if (isIpAccess) {
                    showSuccess(t('common:nginxConfig.applySuccessIpHint', 'SSL configuration applied. If accessing via IP, please switch to your domain URL to avoid browser SSL warnings.'));
                } else {
                    showSuccess(t('common:nginxConfig.applySuccess', 'SSL configuration applied successfully'));
                }
            } else {
                showError(data.error || t('common:nginxConfig.applyFailed', 'Failed to apply SSL configuration'));
            }
        } catch (error) {
            showError(t('common:nginxConfig.applyFailed', 'Failed to apply SSL configuration'));
        } finally {
            setIsApplying(false);
        }
    }, [tenantSlug, user?.id, clearPendingChanges, showSuccess, showError, t]);

    // Don't render if no pending changes
    if (!hasPendingChanges) {
        return null;
    }

    return (
        <button
            onClick={handleApply}
            disabled={isApplying}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-yellow-900 bg-yellow-400 hover:bg-yellow-500 rounded-lg transition-colors animate-pulse disabled:animate-none disabled:opacity-70"
            title={t('common:nginxConfig.pendingChanges', 'SSL configuration changes pending - Click to apply')}
        >
            {isApplying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <AlertTriangle className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{t('common:nginxConfig.applySSL', 'Apply SSL')}</span>
        </button>
    );
}

export default NginxApplyButton;
